import React, { useEffect, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box, Stack, Typography, Avatar, Tooltip,
  Menu, MenuItem, IconButton,
} from "@mui/material";
import { ImAttachment } from "react-icons/im";
import {
  FiCalendar, FiMessageSquare, FiClock,
  FiMoreVertical, FiTrash2, FiRefreshCw, FiActivity, FiZap, FiArchive,
} from "react-icons/fi";
import dayjs from "dayjs";
import { PRIORITY } from "../../../constant/constant";
import AssigneeGroup from "../../../components/AssigneeGroup";
import PrivateBadge from "../../../components/PrivateBadge";
import { isPrivateTask, canSeeAssignees } from "../../../components/AssigneeGroup/utils";

// ─── Drag portal — renders dragged card in <body> to avoid transform ancestors
const getDragPortal = () => {
  if (typeof document === "undefined") return null;
  let el = document.getElementById("rbd-drag-portal");
  if (!el) {
    el = document.createElement("div");
    el.id = "rbd-drag-portal";
    document.body.appendChild(el);
  }
  return el;
};
const dragPortal = getDragPortal();

// ─── StrictMode fix ───────────────────────────────────────────────────────────
const StrictModeDroppable = ({ children, isDropDisabled = false, isCombineEnabled = false, ignoreContainerClipping = false, ...props }) => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => cancelAnimationFrame(animation);
  }, []);
  if (!enabled) return null;
  return (
    <Droppable
      isDropDisabled={isDropDisabled}
      isCombineEnabled={isCombineEnabled}
      ignoreContainerClipping={ignoreContainerClipping}
      {...props}
    >
      {children}
    </Droppable>
  );
};

// ─── Config ────────────────────────────────────────────────────────────────────
const PRIORITY_CONFIG = {
  [PRIORITY.HIGH]: { label: "High", dot: "#EF4444", bg: "#FEF2F2", text: "#DC2626" },
  [PRIORITY.MEDIUM]: { label: "Medium", dot: "#F59E0B", bg: "#FFFBEB", text: "#B45309" },
  [PRIORITY.LOW]: { label: "Low", dot: "#10B981", bg: "#ECFDF5", text: "#059669" },
};

const COLUMN_CONFIG = {
  1: {
    stripe: "#94A3B8",
    headerBg: "#F8FAFC",
    accentBg: "linear-gradient(160deg,#F8FAFC 0%,#F1F5F9 100%)",
    tagBg: "#E2E8F0", tagText: "#64748B",
    dropBg: "rgba(100,116,139,0.05)",
  },
  2: {
    stripe: "#f1e063ff",
    headerBg: "#F5F3FF",
    accentBg: "linear-gradient(160deg,#F5F3FF 0%,#EDE9FE 100%)",
    tagBg: "#DDD6FE", tagText: "#7b6c16",
    dropBg: "rgba(241, 224, 99, 0.06)",
  },
  3: {
    stripe: "#10B981",
    headerBg: "#ECFDF5",
    accentBg: "linear-gradient(160deg,#ECFDF5 0%,#D1FAE5 100%)",
    tagBg: "#A7F3D0", tagText: "#065F46",
    dropBg: "rgba(16,185,129,0.06)",
  },
  /* 4 = Not Assigned */
  4: {
    stripe: "#0D9488",
    headerBg: "#F0FDFA",
    accentBg: "linear-gradient(160deg,#F0FDFA 0%,#CCFBF1 100%)",
    tagBg: "#99F6E4", tagText: "#0F766E",
    dropBg: "rgba(13,148,136,0.06)",
  },
};

// ─── Deadline badge ────────────────────────────────────────────────────────────
const getDeadline = (date, statusId) => {
  if (!date) return null;
  const d = dayjs(date);
  const diff = d.diff(dayjs(), "day");
  if (statusId === 3) return { text: d.format("DD MMM"), color: "#10B981", urgent: false, overdue: false };
  if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, color: "#EF4444", urgent: true, overdue: true };
  if (diff === 0) return { text: "Due today", color: "#F59E0B", urgent: true, overdue: false };
  if (diff <= 3) return { text: `${diff}d left`, color: "#F59E0B", urgent: false, overdue: false };
  return { text: d.format("DD MMM"), color: "#94A3B8", urgent: false, overdue: false };
};

// ─── Sort tasks: overdue → due today → future (nearest first) → no deadline ──
const sortByDeadline = (tasks) => {
  const today = dayjs().startOf("day");
  const rank = (t) => {
    if (!t.actualDeadlineDate) return 4;
    const diff = dayjs(t.actualDeadlineDate).startOf("day").diff(today, "day");
    if (diff < 0) return 0;
    if (diff === 0) return 1;
    return 2;
  };
  return [...tasks].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (a.actualDeadlineDate && b.actualDeadlineDate)
      return dayjs(a.actualDeadlineDate).diff(dayjs(b.actualDeadlineDate));
    return 0;
  });
};

// ─── Task Card ──────────────────────────────────────────────────────────────────
const TaskCard = React.memo(({ task, index, onEditTask, onViewComments, onViewActivity, isAdmin, isPM, currentUserId, onDeleteTask, onArchiveTask, onViewSourceTask, onAssigneeStatusChanged }) => {
  const p = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG[PRIORITY.LOW];
  const deadline = getDeadline(task.actualDeadlineDate, task.status);
  const isCompleted = task.status === 3;

  const creatorName = task.creatorName || task.creatorUserName;

  const canAct = isAdmin || String(task.assignedBy) === String(currentUserId);

  const [menuAnchor, setMenuAnchor] = useState(null);

  const initials = (name) =>
    name
      ? name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("")
      : "";

  return (
    <Draggable draggableId={String(task._id)} index={index}>
      {(provided, snapshot) => {
        const cardNode = (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{ ...provided.draggableProps.style, marginBottom: 8, outline: "none" }}
          >
            <Box
              onClick={() => !snapshot.isDragging && onEditTask(task)}
              sx={{
                position: "relative",
                bgcolor: "#FFFFFF",
                borderRadius: "10px",
                border: "1.5px solid",
                borderColor: snapshot.isDragging ? "#0D9488" : isCompleted ? "#D1FAE5" : "#E8EDF2",
                boxShadow: snapshot.isDragging
                  ? "0 16px 32px -6px rgba(13,148,136,0.18), 0 6px 12px rgba(0,0,0,0.06)"
                  : "0 1px 3px rgba(15,23,42,0.05)",
                cursor: snapshot.isDragging ? "grabbing" : "grab",
                overflow: "hidden",
                transition: "border-color 0.2s, box-shadow 0.18s",
                "&:hover": {
                  borderColor: "#0D9488",
                  boxShadow: "0 4px 16px rgba(13,148,136,0.13)",
                },
              }}
            >
              {/* Priority stripe */}
              <Box
                sx={{
                  position: "absolute",
                  left: 0, top: 0, bottom: 0,
                  width: "3px",
                  bgcolor: p.dot,
                  borderRadius: "10px 0 0 10px",
                }}
              />

              <Box sx={{ pl: "14px", pr: 1, pt: 1.3, pb: 1.2 }}>

                {/* Row 1: task number + s.no + deadline + three-dot menu */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                  <Stack direction="row" alignItems="center" spacing={0.7}>
                    <Typography
                      sx={{
                        fontSize: "10.5px", fontWeight: 700,
                        color: p.dot,
                        letterSpacing: "0.04em",
                        fontFamily: "'Inter', monospace",
                      }}
                    >
                      #{task?.taskNo || "—"}
                    </Typography>
                    {task?.serialNo && (
                      <Box sx={{
                        px: "5px", py: "1px", borderRadius: "4px",
                        bgcolor: "#F0F9FF", border: "1px solid #BAE6FD",
                      }}>
                        <Typography sx={{
                          fontSize: "9px", fontWeight: 700, color: "#0369A1",
                          fontFamily: "ui-monospace, SFMono-Regular, monospace",
                          letterSpacing: "0.04em", lineHeight: 1.5,
                        }}>
                          {task.serialNo}
                        </Typography>
                      </Box>
                    )}
                  </Stack>

                  <Stack direction="row" spacing={0.3} alignItems="center">
                    {/* Recurring badge — R chip with tooltip */}
                    {task.isRecurring && (
                      <Tooltip
                        title={
                          task.recurringStartDate && task.recurringEndDate
                            ? `Recurring: ${dayjs(task.recurringStartDate).format("DD MMM YYYY")} → ${dayjs(task.recurringEndDate).format("DD MMM YYYY")}`
                            : "Recurring Task"
                        }
                        placement="top"
                        arrow
                      >
                        <Box
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            gap: "3px",
                            px: "6px", py: "2px",
                            borderRadius: "20px",
                            bgcolor: "#F0FDFA",
                            border: "1px solid #99D6D1",
                            cursor: "default",
                          }}
                        >
                          <FiRefreshCw size={8} color="#0D9488" />
                          <Typography sx={{ fontSize: "9.5px", fontWeight: 800, color: "#0D9488", fontFamily: "'Inter', sans-serif", letterSpacing: "0.03em" }}>
                            R
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}

                    {task.isCreatedByCron && (
                      <Tooltip
                        title={task.sourceTask ? "Auto-created from recurring task — click to view original" : "Auto-created by recurring schedule"}
                        placement="top"
                        arrow
                      >
                        <Box
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (task.sourceTask) onViewSourceTask?.(task.sourceTask);
                          }}
                          sx={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            gap: "3px",
                            px: "6px", py: "2px",
                            borderRadius: "20px",
                            bgcolor: "#F5F3FF",
                            border: "1px solid #C4B5FD",
                            cursor: task.sourceTask ? "pointer" : "default",
                            transition: "all 0.15s",
                            ...(task.sourceTask && {
                              "&:hover": { bgcolor: "#EDE9FE", borderColor: "#7C3AED" },
                            }),
                          }}
                        >
                          <FiZap size={8} color="#7C3AED" />
                          <Typography sx={{ fontSize: "9.5px", fontWeight: 800, color: "#7C3AED", fontFamily: "'Inter', sans-serif", letterSpacing: "0.03em" }}>
                            C
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}

                    {deadline ? (
                      <Tooltip
                        title={dayjs(task.actualDeadlineDate).format("DD MMM YYYY")}
                        placement="top"
                        arrow
                        disableInteractive
                      >
                      <Stack
                        direction="row" alignItems="center" spacing={0.4}
                        sx={{
                          px: "7px", py: "2px", borderRadius: "20px",
                          bgcolor: deadline.urgent ? `${deadline.color}12` : "#F8FAFC",
                          border: `1px solid ${deadline.urgent ? `${deadline.color}30` : "#E2E8F0"}`,
                          cursor: "default",
                        }}
                      >
                        <FiCalendar size={9} color={deadline.color} />
                        <Typography
                          sx={{
                            fontSize: "10px", fontWeight: 700, color: deadline.color,
                            fontFamily: "'Inter', sans-serif",
                            ...(deadline.urgent && {
                              animation: "urgentPulse 2s ease-in-out infinite",
                              "@keyframes urgentPulse": {
                                "0%,100%": { opacity: 1 },
                                "50%": { opacity: 0.55 },
                              },
                            }),
                          }}
                        >
                          {deadline.text}
                        </Typography>
                      </Stack>
                      </Tooltip>
                    ) : (
                      <Typography sx={{ fontSize: "10px", color: "#CBD5E1", fontFamily: "'Inter', sans-serif" }}>
                        No date
                      </Typography>
                    )}

                    {/* Three-dot menu button */}
                    <IconButton
                      size="small"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuAnchor(e.currentTarget);
                      }}
                      sx={{
                        width: 22, height: 22, borderRadius: "5px",
                        color: "#94A3B8",
                        "&:hover": { bgcolor: "#F1F5F9", color: "#475569" },
                      }}
                    >
                      <FiMoreVertical size={12} />
                    </IconButton>
                  </Stack>
                </Stack>

                {/* Title */}
                <Typography
                  sx={{
                    fontSize: "13px", fontWeight: 600,
                    color: isCompleted ? "#94A3B8" : "#0F172A",
                    lineHeight: 1.4, mb: 0.5,
                    display: "-webkit-box",
                    WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {task?.title || "Untitled"}
                </Typography>

                {/* Description preview */}
                {task?.description && (
                  <Box
                    sx={{
                      fontSize: "11.5px", color: "#94A3B8", lineHeight: 1.4, mb: 0.9,
                      display: "-webkit-box",
                      WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      "& p, & *": { m: "0 !important", fontSize: "11.5px !important", color: "#94A3B8 !important" },
                    }}
                    dangerouslySetInnerHTML={{ __html: task.description }}
                  />
                )}

                {/* Divider */}
                <Box sx={{ height: "1px", bgcolor: "#F1F5F9", mx: -0.5, mb: 0.9 }} />

                {/* Footer */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">

                  {/* Left: priority + meta badges */}
                  <Stack direction="row" spacing={0.7} alignItems="center">
                    <Box
                      sx={{
                        display: "inline-flex", alignItems: "center", gap: "4px",
                        px: "7px", py: "2px", borderRadius: "20px",
                        bgcolor: p.bg,
                      }}
                    >
                      <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: p.dot, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: p.text, fontFamily: "'Inter', sans-serif" }}>
                        {p.label}
                      </Typography>
                    </Box>

                    <Stack
                      direction="row" alignItems="center" spacing={0.4}
                      sx={{ px: 0.7, py: "2px", borderRadius: "5px", bgcolor: "#F8FAFC", border: "1px solid #E2E8F0" }}
                    >
                      <ImAttachment size={9} color="#94A3B8" />
                      <Typography sx={{ fontSize: "10px", color: "#94A3B8", fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>
                        {task.fileCount ?? 0}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row" alignItems="center" spacing={0.4}
                      sx={{ px: 0.7, py: "2px", borderRadius: "5px", bgcolor: "#F8FAFC", border: "1px solid #E2E8F0" }}
                    >
                      <FiMessageSquare size={9} color="#94A3B8" />
                      <Typography sx={{ fontSize: "10px", color: "#94A3B8", fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>
                        {task.commentsCount ?? 0}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row" alignItems="center" spacing={0.4}
                      sx={{ px: 0.7, py: "2px", borderRadius: "5px", bgcolor: "#F0FDFA", border: "1px solid #CCFBF1" }}
                    >
                      <FiClock size={9} color="#0D9488" />
                      <Typography sx={{ fontSize: "10px", color: "#0D9488", fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>
                        {task.workLogCount ?? 0}
                      </Typography>
                    </Stack>
                  </Stack>

                  {/* Right: assigner → assignees */}
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {canSeeAssignees(task, { currentUserId, isAdmin }) && isPrivateTask(task) && (
                      <PrivateBadge compact onMouseDown={(e) => e.stopPropagation()} />
                    )}
                    {creatorName && (
                      <Tooltip title={`Assigned by: ${creatorName}`} placement="top" arrow disableInteractive>
                        <span style={{ display: "inline-flex" }}>
                          <Avatar
                            sx={{
                              width: 24, height: 24,
                              fontSize: "9px", fontWeight: 700,
                              bgcolor: "#799081ff",
                              color: "#fff",
                              border: "2px solid #fff",
                              boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                            }}
                          >
                            {initials(creatorName)}
                          </Avatar>
                        </span>
                      </Tooltip>
                    )}
                    {creatorName && (
                      <Typography sx={{ fontSize: "10px", color: "#CBD5E1", lineHeight: 1, fontWeight: 600 }}>
                        →
                      </Typography>
                    )}
                    <AssigneeGroup
                      task={task}
                      taskId={task._id}
                      assignedBy={task.assignedBy}
                      size={24}
                      max={3}
                      placement="top"
                      disabled={snapshot.isDragging}
                      isAdmin={isAdmin}
                      currentUserId={currentUserId}
                      onChanged={onAssigneeStatusChanged}
                    />
                  </Stack>

                </Stack>
              </Box>
            </Box>

          {/* Three-dot context menu */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{
              sx: {
                borderRadius: "10px", minWidth: 180,
                boxShadow: "0 8px 24px rgba(15,23,42,0.14)",
                border: "1px solid #E2E8F0",
              },
            }}
          >
            {/* Overdue banner */}
            {deadline?.overdue && (
              <Box
                sx={{
                  px: 2, py: 1,
                  bgcolor: "#FEF2F2",
                  borderBottom: "1px solid #FECACA",
                }}
              >
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <FiClock size={12} color="#DC2626" />
                  <Typography
                    sx={{
                      fontSize: "11px", fontWeight: 700, color: "#DC2626",
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    {deadline.text}
                  </Typography>
                </Stack>
              </Box>
            )}

            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onViewComments?.(task);
              }}
              sx={{
                fontSize: "13px", gap: 1.2, py: 1,
                fontFamily: "'Inter', sans-serif", fontWeight: 500,
                "&:hover": { bgcolor: "#F0FDFA", color: "#0D9488" },
              }}
            >
              <FiMessageSquare size={13} />
              Comments
            </MenuItem>

            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onViewActivity?.(task);
              }}
              sx={{
                fontSize: "13px", gap: 1.2, py: 1,
                fontFamily: "'Inter', sans-serif", fontWeight: 500,
                "&:hover": { bgcolor: "#F0FDFA", color: "#0D9488" },
              }}
            >
              <FiActivity size={13} />
              Activity
            </MenuItem>

            {canAct && (
            <MenuItem
              onClick={() => { setMenuAnchor(null); onArchiveTask?.(task); }}
              sx={{
                fontSize: "13px", gap: 1.2, py: 1,
                color: "#D97706", fontFamily: "'Inter', sans-serif", fontWeight: 500,
                "&:hover": { bgcolor: "#FFFBEB" },
              }}
            >
              <FiArchive size={13} />
              Archive
            </MenuItem>
            )}

            {canAct && (
              <MenuItem
                onClick={() => { setMenuAnchor(null); onDeleteTask?.(task); }}
                sx={{
                  fontSize: "13px", gap: 1.2, py: 1,
                  color: "#DC2626", fontFamily: "'Inter', sans-serif", fontWeight: 500,
                  "&:hover": { bgcolor: "#FEF2F2" },
                }}
              >
                <FiTrash2 size={13} />
                Delete
              </MenuItem>
            )}
          </Menu>
        </div>
        );

        // Portal the card into <body> while dragging to avoid
        // position:fixed being offset by any ancestor CSS transform
        return snapshot.isDragging && dragPortal
          ? ReactDOM.createPortal(cardNode, dragPortal)
          : cardNode;
      }}
    </Draggable>
  );
});

// ─── Empty column placeholder ─────────────────────────────────────────────────
const EmptyColumn = ({ config }) => (
  <Box
    sx={{
      mx: 0.5, py: 5, px: 2,
      borderRadius: "10px",
      border: "2px dashed #E8EDF2",
      display: "flex", flexDirection: "column",
      alignItems: "center", gap: 1,
    }}
  >
    <Box
      sx={{
        width: 32, height: 32, borderRadius: "8px",
        bgcolor: `${config.stripe}14`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <Box sx={{ width: 12, height: 12, border: `2px dashed ${config.stripe}50`, borderRadius: "3px" }} />
    </Box>
    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#CBD5E1", fontFamily: "'Inter', sans-serif" }}>
      No tasks here
    </Typography>
    <Typography sx={{ fontSize: "11px", color: "#E2E8F0", fontFamily: "'Inter', sans-serif" }}>
      Drag a card or create one
    </Typography>
  </Box>
);

// ─── Load More Button ─────────────────────────────────────────────────────────
const LoadMoreButton = ({ statusId, loading, onLoadMore }) => (
  <Box sx={{ px: 0.5, pb: 1, pt: 0.5 }}>
    <Box
      component="button"
      onClick={() => onLoadMore(statusId)}
      disabled={loading}
      sx={{
        width: "100%", py: "8px",
        border: "1.5px dashed #CBD5E1",
        borderRadius: "9px",
        bgcolor: loading ? "#F8FAFC" : "transparent",
        color: loading ? "#94A3B8" : "#64748B",
        fontSize: "12px", fontWeight: 600, fontFamily: "inherit",
        cursor: loading ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
        transition: "all 0.15s",
        "&:hover:not(:disabled)": {
          bgcolor: "#F0FDFA", borderColor: "#0D9488", color: "#0D9488",
        },
      }}
    >
      {loading ? (
        <>
          <Box
            sx={{
              width: 12, height: 12, borderRadius: "50%",
              border: "2px solid #CBD5E1", borderTopColor: "#0D9488",
              animation: "spin 0.7s linear infinite",
              "@keyframes spin": { to: { transform: "rotate(360deg)" } },
            }}
          />
          Loading…
        </>
      ) : (
        <>
          <Box sx={{ fontSize: "14px", lineHeight: 1 }}>⬇</Box>
          Load next 20
        </>
      )}
    </Box>
  </Box>
);

// ─── Kanban Column (memoized) ─────────────────────────────────────────────────
// Each column is isolated: it only re-renders when its own tasks/hasMore/count
// change. Columns that aren't the drag source or destination stay frozen during
// a drag, eliminating the main cause of drag lag.
const KanbanColumn = React.memo(({
  column,
  colIndex,
  tasks,
  hasMore,
  loadingMore,
  totalCount,
  onEditTask,
  onViewComments,
  onViewActivity,
  isAdmin,
  isPM,
  currentUserId,
  onDeleteTask,
  onArchiveTask,
  onLoadMore,
  onViewSourceTask,
  onAssigneeStatusChanged,
}) => {
  const cfg = COLUMN_CONFIG[column._id] || COLUMN_CONFIG[1];

  // Sorted once per tasks-array change, not on every parent render
  const columnTasks = useMemo(() => sortByDeadline(tasks), [tasks]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.22, delay: colIndex * 0.07 }}
      style={{ flex: "0 0 310px", width: 310 }}
    >
      <Box
        sx={{
          display: "flex", flexDirection: "column",
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 2px 12px rgba(15,23,42,0.06)",
          background: cfg.accentBg,
          height: "calc(100vh - 120px)",
          overflow: "hidden",
        }}
      >
        {/* Column header */}
        <Box
          sx={{
            px: 2.5, py: 1.8,
            bgcolor: cfg.headerBg,
            borderBottom: "1px solid #E8EDF2",
            flexShrink: 0,
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: "2.5px",
              bgcolor: cfg.stripe,
              borderRadius: "16px 16px 0 0",
            },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Box
                sx={{
                  width: 9, height: 9, borderRadius: "50%",
                  bgcolor: cfg.stripe,
                  boxShadow: `0 0 0 3px ${cfg.stripe}22`,
                }}
              />
              <Typography
                sx={{
                  fontWeight: 700, fontSize: "13px", color: "#0F172A",
                  letterSpacing: "0.01em", fontFamily: "'Inter', sans-serif",
                }}
              >
                {column.name}
              </Typography>
            </Stack>

            <AnimatePresence mode="wait">
              <motion.div
                key={totalCount}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.6, opacity: 0 }}
                transition={{ type: "spring", stiffness: 460, damping: 22 }}
              >
                <Box
                  sx={{
                    px: 1.2, py: "2px", borderRadius: "20px",
                    bgcolor: cfg.tagBg,
                    border: `1px solid ${cfg.tagText}22`,
                    minWidth: 24, textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px", fontWeight: 800, color: cfg.tagText,
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    {totalCount}
                  </Typography>
                </Box>
              </motion.div>
            </AnimatePresence>
          </Stack>
        </Box>

        {/* Droppable area */}
        <StrictModeDroppable droppableId={`status-${column._id}`} direction="vertical">
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                flexGrow: 1,
                display: "block",
                overflowY: "auto",
                p: 1.5,
                minHeight: 120,
                bgcolor: snapshot.isDraggingOver ? cfg.dropBg : "transparent",
                transition: "background-color 0.2s ease",
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "2px" },
                "&::-webkit-scrollbar-thumb:hover": { background: "#94A3B8" },
              }}
            >
              {columnTasks.length === 0 && !snapshot.isDraggingOver
                ? <EmptyColumn config={cfg} />
                : columnTasks.map((task, idx) => (
                  <TaskCard
                    key={task._id}
                    task={task}
                    index={idx}
                    onEditTask={onEditTask}
                    onViewComments={onViewComments}
                    onViewActivity={onViewActivity}
                    isAdmin={isAdmin}
                    isPM={isPM}
                    currentUserId={currentUserId}
                    onDeleteTask={onDeleteTask}
                    onArchiveTask={onArchiveTask}
                    onViewSourceTask={onViewSourceTask}
                    onAssigneeStatusChanged={onAssigneeStatusChanged}
                  />
                ))
              }
              {provided.placeholder}

              {hasMore && (
                <LoadMoreButton
                  statusId={column._id}
                  loading={loadingMore}
                  onLoadMore={onLoadMore}
                />
              )}
            </Box>
          )}
        </StrictModeDroppable>
      </Box>
    </motion.div>
  );
});

// ─── Board ─────────────────────────────────────────────────────────────────────
const TodoTaskDragSection = ({
  tasksByStatus = {},
  hasMoreByStatus = {},
  loadingMoreByStatus = {},
  totalCountByStatus = {},
  onLoadMore,
  statuses,
  onEditTask,
  onViewComments,
  onViewActivity,
  isAdmin = false,
  isPM = false,
  currentUserId = null,
  onDeleteTask,
  onArchiveTask,
  onViewSourceTask,
  onAssigneeStatusChanged,
}) => {

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2.5,
        overflowX: "auto",
        alignItems: "flex-start",
        pb: 2,
        "&::-webkit-scrollbar": { height: "5px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "3px" },
        "&::-webkit-scrollbar-thumb:hover": { background: "#94A3B8" },
      }}
    >
      {statuses.map((column, colIndex) => (
        <KanbanColumn
          key={column._id}
          column={column}
          colIndex={colIndex}
          tasks={tasksByStatus[column._id] || []}
          hasMore={hasMoreByStatus[column._id] ?? false}
          loadingMore={loadingMoreByStatus[column._id] ?? false}
          totalCount={totalCountByStatus[column._id] ?? 0}
          onEditTask={onEditTask}
          onViewComments={onViewComments}
          onViewActivity={onViewActivity}
          isAdmin={isAdmin}
          isPM={isPM}
          currentUserId={currentUserId}
          onDeleteTask={onDeleteTask}
          onArchiveTask={onArchiveTask}
          onLoadMore={onLoadMore}
          onViewSourceTask={onViewSourceTask}
          onAssigneeStatusChanged={onAssigneeStatusChanged}
        />
      ))}

    </Box>
  );
};

export default React.memo(TodoTaskDragSection);
